import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../utils/firebase";
import "../pages/Admin.css";

// Gate for the /admin section. Checks both Firebase Auth state AND the
// 'admin: true' custom claim on the ID token. The claim is granted manually
// after the account is created, so a signed-in-but-not-yet-claimed state is
// expected and must be surfaced clearly rather than silently redirected.
const RequireAdmin = ({ children }) => {
  const [status, setStatus] = useState("loading"); // loading | authorized | unauthenticated | forbidden

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setStatus("unauthenticated");
        return;
      }

      try {
        // Force a fresh token: the cached one can be up to an hour old, so
        // right after the admin claim is granted this would otherwise still
        // read as "forbidden" until the cache naturally expires.
        const tokenResult = await user.getIdTokenResult(true);
        if (tokenResult.claims && tokenResult.claims.admin === true) {
          setStatus("authorized");
        } else {
          setStatus("forbidden");
        }
      } catch (err) {
        console.error("Failed to check admin claims:", err);
        setStatus("forbidden");
      }
    });

    return () => unsubscribe();
  }, []);

  if (status === "loading") {
    return <div className="admin-loading-page">Checking admin access...</div>;
  }

  if (status === "unauthenticated") {
    return <Navigate to="/admin/login" replace />;
  }

  if (status === "forbidden") {
    return (
      <div className="admin-loading-page">
        <div className="admin-alert admin-alert-info" style={{ maxWidth: 420 }}>
          Your account doesn't have admin access yet. If you believe this is a
          mistake, contact the project owner to have the admin permission
          granted to your account.
        </div>
      </div>
    );
  }

  return children;
};

export default RequireAdmin;
